import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
} from "react-router-dom";
import { lazy, Suspense, type ComponentType } from "react";
import { AppLayout } from "@/components/layout/app-layout";
import { AuthLayout } from "@/components/layout/auth-layout";
import { PrivateRoute } from "./private-route";
import { PublicRoute } from "./public-route";
import { Spinner } from "@/components/ui/spinner";
import {
  DashboardSkeleton,
  ListSkeleton,
  VaultDetailSkeleton,
  ItemFormSkeleton,
  SettingsSkeleton,
  SettingsSubPageSkeleton,
} from "@/components/skeletons";

// Auto-reload on stale chunk after deploy (prevents "Failed to fetch dynamically imported module")
function lazyWithRetry(factory: () => Promise<{ default: ComponentType }>) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (error) {
      if (
        error instanceof TypeError &&
        error.message.includes("dynamically imported module")
      ) {
        const lastReload = Number(
          sessionStorage.getItem("chunk-reload-at") || "0",
        );
        if (Date.now() - lastReload > 10_000) {
          sessionStorage.setItem("chunk-reload-at", String(Date.now()));
          window.location.reload();
          return new Promise(() => {});
        }
      }
      throw error;
    }
  });
}

// Lazy load auth pages
const Login = lazyWithRetry(() => import("@/pages/auth/login"));
const Register = lazyWithRetry(() => import("@/pages/auth/register"));
const Unlock = lazyWithRetry(() => import("@/pages/auth/unlock"));
const ForgotPassword = lazyWithRetry(
  () => import("@/pages/auth/forgot-password"),
);
const VerifyEmail = lazyWithRetry(() => import("@/pages/auth/verify-email"));

// Lazy load app pages
const Dashboard = lazyWithRetry(() => import("@/pages/dashboard"));
const Vault = lazyWithRetry(() => import("@/pages/vault"));
const VaultDetail = lazyWithRetry(() => import("@/pages/vault/[id]"));
const ItemNew = lazyWithRetry(() => import("@/pages/item/new"));
const ItemDetail = lazyWithRetry(() => import("@/pages/item/[id]"));
const Generator = lazyWithRetry(() => import("@/pages/generator"));
const Settings = lazyWithRetry(() => import("@/pages/settings"));
const SettingsBilling = lazyWithRetry(() => import("@/pages/settings/billing"));
const SettingsBillingPlans = lazyWithRetry(
  () => import("@/pages/settings/billing/plans"),
);
const SettingsBillingPayments = lazyWithRetry(
  () => import("@/pages/settings/billing/payments"),
);
const SettingsBillingManage = lazyWithRetry(
  () => import("@/pages/settings/billing/manage"),
);
const SettingsSecurity = lazyWithRetry(
  () => import("@/pages/settings/security"),
);
const SettingsSessions = lazyWithRetry(
  () => import("@/pages/settings/sessions"),
);
const SettingsFamily = lazyWithRetry(() => import("@/pages/settings/family"));
const SettingsEmergency = lazyWithRetry(
  () => import("@/pages/settings/emergency-contacts"),
);
const SettingsImport = lazyWithRetry(() => import("@/pages/settings/import"));
const SettingsExport = lazyWithRetry(() => import("@/pages/settings/export"));
const SettingsPasskeys = lazyWithRetry(
  () => import("@/pages/settings/passkeys"),
);
const SettingsApiTokens = lazyWithRetry(
  () => import("@/pages/settings/api-tokens"),
);
const SettingsApiDocs = lazyWithRetry(
  () => import("@/pages/settings/api-docs"),
);
const AuthenticatorPage = lazyWithRetry(
  () => import("@/pages/authenticator"),
);
const SecurityReport = lazyWithRetry(() => import("@/pages/security-report"));
const Trash = lazyWithRetry(() => import("@/pages/trash"));
const Downloads = lazyWithRetry(() => import("@/pages/downloads"));

const Loading = () => (
  <div className="flex h-screen items-center justify-center">
    <Spinner size="lg" />
  </div>
);

const router = createBrowserRouter([
  // Redirect root to login
  {
    path: "/",
    element: <Navigate to="/login" replace />,
  },

  // Public routes (auth)
  {
    element: <PublicRoute />,
    children: [
      {
        element: <AuthLayout />,
        children: [
          { path: "/login", element: <Login /> },
          { path: "/register", element: <Register /> },
          { path: "/forgot-password", element: <ForgotPassword /> },
          { path: "/verify-email", element: <VerifyEmail /> },
        ],
      },
    ],
  },

  // Unlock route (authenticated but locked)
  {
    path: "/unlock",
    element: (
      <Suspense fallback={<Loading />}>
        <Unlock />
      </Suspense>
    ),
  },

  // Private routes (authenticated and unlocked)
  {
    element: <PrivateRoute />,
    children: [
      {
        element: <AppLayout />,
        children: [
          {
            path: "/app",
            element: (
              <Suspense fallback={<DashboardSkeleton />}>
                <Dashboard />
              </Suspense>
            ),
          },
          {
            path: "/app/vault",
            element: (
              <Suspense fallback={<ListSkeleton />}>
                <Vault />
              </Suspense>
            ),
          },
          {
            path: "/app/vault/:id",
            element: (
              <Suspense fallback={<VaultDetailSkeleton />}>
                <VaultDetail />
              </Suspense>
            ),
          },
          {
            path: "/app/item/new",
            element: (
              <Suspense fallback={<ItemFormSkeleton />}>
                <ItemNew />
              </Suspense>
            ),
          },
          {
            path: "/app/item/:id",
            element: (
              <Suspense fallback={<ItemFormSkeleton />}>
                <ItemDetail />
              </Suspense>
            ),
          },
          {
            path: "/app/generator",
            element: (
              <Suspense fallback={<SettingsSubPageSkeleton />}>
                <Generator />
              </Suspense>
            ),
          },
          {
            path: "/app/authenticator",
            element: (
              <Suspense fallback={<SettingsSubPageSkeleton />}>
                <AuthenticatorPage />
              </Suspense>
            ),
          },
          {
            path: "/app/settings",
            element: (
              <Suspense fallback={<SettingsSkeleton />}>
                <Settings />
              </Suspense>
            ),
          },
          {
            path: "/app/settings/billing",
            element: (
              <Suspense fallback={<SettingsSubPageSkeleton />}>
                <SettingsBilling />
              </Suspense>
            ),
          },
          {
            path: "/app/settings/billing/plans",
            element: (
              <Suspense fallback={<SettingsSubPageSkeleton />}>
                <SettingsBillingPlans />
              </Suspense>
            ),
          },
          {
            path: "/app/settings/billing/payments",
            element: (
              <Suspense fallback={<SettingsSubPageSkeleton />}>
                <SettingsBillingPayments />
              </Suspense>
            ),
          },
          {
            path: "/app/settings/billing/manage",
            element: (
              <Suspense fallback={<SettingsSubPageSkeleton />}>
                <SettingsBillingManage />
              </Suspense>
            ),
          },
          {
            path: "/app/settings/security",
            element: (
              <Suspense fallback={<SettingsSubPageSkeleton />}>
                <SettingsSecurity />
              </Suspense>
            ),
          },
          {
            path: "/app/settings/sessions",
            element: (
              <Suspense fallback={<SettingsSubPageSkeleton />}>
                <SettingsSessions />
              </Suspense>
            ),
          },
          {
            path: "/app/settings/family",
            element: (
              <Suspense fallback={<SettingsSubPageSkeleton />}>
                <SettingsFamily />
              </Suspense>
            ),
          },
          {
            path: "/app/settings/emergency-contacts",
            element: (
              <Suspense fallback={<SettingsSubPageSkeleton />}>
                <SettingsEmergency />
              </Suspense>
            ),
          },
          {
            path: "/app/settings/import",
            element: (
              <Suspense fallback={<SettingsSubPageSkeleton />}>
                <SettingsImport />
              </Suspense>
            ),
          },
          {
            path: "/app/settings/export",
            element: (
              <Suspense fallback={<SettingsSubPageSkeleton />}>
                <SettingsExport />
              </Suspense>
            ),
          },
          {
            path: "/app/settings/passkeys",
            element: (
              <Suspense fallback={<SettingsSubPageSkeleton />}>
                <SettingsPasskeys />
              </Suspense>
            ),
          },
          {
            path: "/app/settings/api-tokens",
            element: (
              <Suspense fallback={<SettingsSubPageSkeleton />}>
                <SettingsApiTokens />
              </Suspense>
            ),
          },
          {
            path: "/app/settings/api-docs",
            element: (
              <Suspense fallback={<SettingsSubPageSkeleton />}>
                <SettingsApiDocs />
              </Suspense>
            ),
          },
          {
            path: "/app/security-report",
            element: (
              <Suspense fallback={<SettingsSubPageSkeleton />}>
                <SecurityReport />
              </Suspense>
            ),
          },
          {
            path: "/app/trash",
            element: (
              <Suspense fallback={<ListSkeleton />}>
                <Trash />
              </Suspense>
            ),
          },
          {
            path: "/app/downloads",
            element: (
              <Suspense fallback={<SettingsSubPageSkeleton />}>
                <Downloads />
              </Suspense>
            ),
          },
        ],
      },
    ],
  },
]);

export function Routes() {
  return (
    <Suspense fallback={<Loading />}>
      <RouterProvider router={router} />
    </Suspense>
  );
}
